import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import pkgCountryCodes from 'countries-code'
import { useWatch } from 'react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getCurrentLanguage } from 'src/Libs/languages/functions/default'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { getTimezonesForCountry } from 'countries-and-timezones'
import { FlowMap, MapCoordinates } from '../../components/Map'
import { MAP_HEIGHT_PX } from '../../constants'
import { countryCodes } from 'src/Libs/flow/constants/countries'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { getAddress } from '../api/address.api'
import { useCurrentAccountLink, useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { countryCodesMap } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useUserSettings } from 'src/Hooks'

interface LocationMapProps {
  googleAgreed: boolean
  isLoading: boolean
}

export const LocationMap: React.FC<LocationMapProps> = ({ googleAgreed, isLoading: propsIsLoading }) => {
  const { t } = useCustomTranslation()
  const selectedAppLanguage = getCurrentLanguage()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const { updateUserSettings, isUserSettingsUpdating } = useUserSettings()

  const { control, setValue, getValues } = useInstallationForm()
  const installationId = useWatch({ control, name: 'id' })
  const [gpsLatitude, gpsLongitude] = useWatch({
    control,
    name: ['gpsLatitude', 'gpsLongitude'],
  })

  const isNewInstallation = installationId === null

  const accountLink = useCurrentAccountLink(installationId ?? undefined)
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const hasWritePermission = canEdit && accountLink?.storagePermissionType === 'rw'

  const handleMapPermissions = (isAgreed: boolean): void => {
    updateUserSettings({ googleAgreed: isAgreed })
      .then(() => {
        enqueueSuccessStack(t('user.googleAgreement.success'))
      })
      .catch(() => {
        enqueueErrorStack(t('user.googleAgreement.fail'))
      })
  }

  const isLoading = propsIsLoading || isUserSettingsUpdating

  useEffect(() => {
    if (!isNewInstallation || !navigator.geolocation || !googleAgreed) return

    navigator.geolocation.getCurrentPosition(result => {
      getAddress(result.coords.latitude, result.coords.longitude, authFetch).then(response => {
        if (!response.value) return

        const initialCountryCode = getValues('generalSettings.location.country')
        const initialTimeZone = getValues('generalSettings.location.timeZone')

        const countryCode =
          response.value.countryCode ||
          initialCountryCode || // fallback 1, the initial country code
          countryCodesMap[selectedAppLanguage.toUpperCase()] || // fallback 2, the user's language country code
          countryCodes.germany // fallback 3, the default Germany country code

        const convertedCountryCode = pkgCountryCodes.convertAlphaCode(countryCode)

        const timeZone = (getTimezonesForCountry(convertedCountryCode) ?? [])[0]?.name || initialTimeZone

        setValue(
          'generalSettings.location',
          {
            country: countryCode,
            postalCode: response.value.postalCode,
            houseNumber: response.value.houseNumber,
            street: response.value.street,
            timeZone,
            location: response.value.city,
          },
          { shouldDirty: true }
        )
      })
    })
    // eslint-disable-next-line
  }, [isNewInstallation, selectedAppLanguage, googleAgreed])

  const clearLocation = (): void => {
    setValue('generalSettings.location.street', '', { shouldDirty: true })
    setValue('generalSettings.location.houseNumber', '', { shouldDirty: true })
    setValue('generalSettings.location.postalCode', '', { shouldDirty: true })
    setValue('generalSettings.location.location', '', { shouldDirty: true })
  }

  const coordinates: MapCoordinates | undefined = (function () {
    if (!gpsLatitude || !gpsLongitude) return

    const lat = Number(gpsLatitude)
    const lng = Number(gpsLongitude)

    if (isNaN(lat) || isNaN(lng)) return

    return { lat, lng }
  })()

  return (
    <Box
      sx={{
        height: MAP_HEIGHT_PX,
        position: { xs: 'relative', md: undefined },
      }}
    >
      <FlowMap
        key={`${coordinates?.lat}-${coordinates?.lng}`}
        coordinates={coordinates}
        disabled={!googleAgreed}
        disabledOptions={{
          text: t('framework.acceptGoogleMap.text'),
          buttonLabel: t('framework.acceptGoogleMap.buttonText'),
          loading: isLoading,
          onClick: () => handleMapPermissions(true),
          disagree: () => handleMapPermissions(false),
        }}
        onGoogleReject={hasWritePermission ? clearLocation : undefined}
      />
    </Box>
  )
}
